import { AccordionSection, Flex, InputCheckbox, InputRange, Label, Span, Switcher } from "@goodfill/ui-components";
import { useState } from "react";
import { RATINGS_REVERSED_LIST } from "@goodfill/domain-properties";
import { PricedBond } from "../../../../PricedBond";
import { BondFilterProps, BondFilter, BondFilterData, BondFilterChipData } from "../../BondFilter";
import { BondFilterSectionMargin, BondFilterSectionGap } from "../../BondFilterConst";
import { BondFilterCriteria } from "../../BondFilterCriteria";
import { BondFilterDataConstraints } from "../../BondFilterDataConstraints";
import { InfoTooltip } from "../../../../../../ui-components/tooltip/InfoTooltip";
import {
  getSessionStorageBoolean,
  getSessionStorageNumber,
  getSessionStorageString,
  removeItemFromSessionStorage,
  saveStringToSessionStorage,
  saveNumberToSessionStorage,
  saveBooleanToSessionStorage,
} from "../../../../../../sessionStorage";
import { getBondRatingIndicesWithOffsetForMissingRating } from "../../../../bondRating/getBondRatingIndicesWithOffsetForMissingRating";

const FILTER_MOODY_STORAGE_KEY = "filter:moodysRatingIndex";
const FILTER_SP_STORAGE_KEY = "filter:sAndPratingIndex";
const FILTER_MOODY_AND_SP_STORAGE_KEY = "filter:moodysOrSandPRatingIndex";
const FILTER_SEPARATE_STORAGE_KEY = "filter:separateRatings";
const FILTER_IS_AND_STORAGE_KEY = "filter:ratingIsAnd";

const moodysLabel = "Moody's";
const sAndPLabel = "S&P";

const _Component = (props: BondFilterProps) => {
  const isSameAsDefault = (criteria: BondFilterCriteria) => {
    return (
      criteria.moodysRatingIndex === props.defaultCriteria.moodysRatingIndex &&
      criteria.sAndPratingIndex === props.defaultCriteria.sAndPratingIndex &&
      criteria.moodysOrSandPRatingIndex === props.defaultCriteria.moodysOrSandPRatingIndex
      // intentionally not `separateRatings` and `bothOrOne`
    );
  };

  const doOnChange = (newCriteria: BondFilterCriteria) =>
    props.onChange({
      criteria: newCriteria,
    });

  const setMoodysRatingIndex = (value: number) => doOnChange({ ...props.criteria, moodysRatingIndex: value });
  const setSandPratingIndex = (value: number) => doOnChange({ ...props.criteria, sAndPratingIndex: value });
  const setMoodysOrSandPRatingIndex = (value: number) =>
    doOnChange({ ...props.criteria, moodysOrSandPRatingIndex: value });
  const onChangeSeparateRatings = () =>
    doOnChange({
      ...props.criteria,
      separateRatings: !props.criteria.separateRatings,
    });

  const onRatingIsAndChange = () =>
    doOnChange({
      ...props.criteria,
      bothOrOne: props.criteria.bothOrOne === "both" ? "one" : "both",
    });

  const [ratingsSectionIsOpen, setRatingsSectionIsOpen] = useState(false);

  const selectedMoodysRatingLowerBound = RATINGS_REVERSED_LIST.moodys[props.criteria.moodysRatingIndex] ?? "";
  const selectedSandPratingLowerBound = RATINGS_REVERSED_LIST.sAndP[props.criteria.sAndPratingIndex] ?? "";
  const selectedMoodysOrSAndPratingLowerBound =
    RATINGS_REVERSED_LIST.moodysOrSandP[props.criteria.moodysOrSandPRatingIndex] ?? "";

  return (
    <AccordionSection title={"Ratings"} isOpen={ratingsSectionIsOpen} setIsOpen={setRatingsSectionIsOpen}>
      <Flex direction="col" verticalMargin={BondFilterSectionMargin} gap={BondFilterSectionGap}>
        <Flex direction="row" justify="start" verticalMargin={2}>
          <InputCheckbox
            id="separateRatingsToggle"
            checked={props.criteria.separateRatings}
            onChange={onChangeSeparateRatings}
          />
          <Label
            uppercase={true}
            htmlFor="separateRatingsToggle"
            accent={!props.criteria.separateRatings}
            primary={props.criteria.separateRatings}
            size="sm"
            style={{ paddingLeft: "5px" }}
          >
            Separate ratings agencies
            <InfoTooltip
              verticalOffset="0"
              text="If a bond is not rated by one agency, a rating of 2 notches below the other agency is assumed for filter and sorting purposes only."
            />
          </Label>
        </Flex>
        {!props.criteria.separateRatings ? (
          <Flex direction="row" justify="start" verticalMargin={2} items={"center"}>
            <Switcher
              id="oneOrBoth"
              checked={props.criteria.bothOrOne === "both"}
              onChange={onRatingIsAndChange}
              label={props.criteria.bothOrOne === "both" ? "Both" : "One"}
            />
          </Flex>
        ) : null}

        {props.criteria.separateRatings ? (
          <>
            <div>
              <Span>
                {moodysLabel}: {selectedMoodysRatingLowerBound}
              </Span>
              <InputRange
                min={props.constraints.ratingIndexMin}
                max={props.constraints.ratingIndexMax}
                leftBound={props.criteria.moodysRatingIndex}
                setLeftBound={setMoodysRatingIndex}
              />
            </div>

            <div>
              <Span>
                {sAndPLabel}: {selectedSandPratingLowerBound}
              </Span>
              <InputRange
                min={props.constraints.ratingIndexMin}
                max={props.constraints.ratingIndexMax}
                leftBound={props.criteria.sAndPratingIndex}
                setLeftBound={setSandPratingIndex}
              />
            </div>
          </>
        ) : (
          <div>
            <Span>
              {moodysLabel}, {sAndPLabel}: {selectedMoodysOrSAndPratingLowerBound[0]},{" "}
              {selectedMoodysOrSAndPratingLowerBound[1]}
            </Span>
            <InputRange
              min={props.constraints.ratingIndexMin}
              max={props.constraints.ratingIndexMax}
              leftBound={props.criteria.moodysOrSandPRatingIndex}
              setLeftBound={setMoodysOrSandPRatingIndex}
            />
          </div>
        )}
      </Flex>
    </AccordionSection>
  );
};

export class BondFilterRating implements BondFilter {
  isVisibleInDropdown(): boolean {
    return true;
  }

  reset(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterCriteria {
    return {
      ...criteria,
      moodysRatingIndex: defaultCriteria.moodysRatingIndex,
      sAndPratingIndex: defaultCriteria.sAndPratingIndex,
      moodysOrSandPRatingIndex: defaultCriteria.moodysOrSandPRatingIndex,
      // Intentionally NO `separateRatings` and `ratingIsAnd`
    };
  }

  getChips(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): BondFilterChipData[] {
    const isActive =
      criteria.moodysRatingIndex !== defaultCriteria.moodysRatingIndex ||
      criteria.sAndPratingIndex !== defaultCriteria.sAndPratingIndex ||
      criteria.moodysOrSandPRatingIndex !== defaultCriteria.moodysOrSandPRatingIndex;

    return isActive
      ? [
          {
            id: "ratings",
            label: "Ratings",
            onClick: () => this.reset(criteria, defaultCriteria),
          },
        ]
      : [];
  }

  getDefaultCriteriaConstraints(pricedBond: PricedBond): Partial<BondFilterDataConstraints> {
    const { moodysIndex, spIndex } = getBondRatingIndicesWithOffsetForMissingRating(pricedBond);

    return {
      ratingIndexMin: Math.min(moodysIndex, spIndex),
      ratingIndexMax: Math.max(moodysIndex, spIndex),
    };
  }

  updateConstraintByPricedBond(pricedBond: PricedBond, current: BondFilterDataConstraints): BondFilterDataConstraints {
    const { moodysIndex, spIndex } = getBondRatingIndicesWithOffsetForMissingRating(pricedBond);

    return {
      ...current,
      ratingIndexMin: Math.min(current.ratingIndexMin, moodysIndex, spIndex),
      ratingIndexMax: Math.max(current.ratingIndexMax, moodysIndex, spIndex),
    };
  }

  afterCriteriaCalculation(current: BondFilterDataConstraints): BondFilterDataConstraints {
    return current;
  }

  evaluate(
    data: BondFilterData,
    criteria: Pick<
      BondFilterCriteria,
      "separateRatings" | "moodysRatingIndex" | "sAndPratingIndex" | "bothOrOne" | "moodysOrSandPRatingIndex"
    >,
  ): boolean {
    const { moodysIndex, spIndex } = getBondRatingIndicesWithOffsetForMissingRating(data.pricedBond);

    if (criteria.separateRatings) {
      const matchesMoody = moodysIndex >= criteria.moodysRatingIndex;
      const matchesSp = spIndex >= criteria.sAndPratingIndex;

      return matchesMoody && matchesSp;
    } else {
      const parts = [moodysIndex, spIndex].map((i) => i >= criteria.moodysOrSandPRatingIndex);

      return criteria.bothOrOne === "both"
        ? parts.every((boolResult) => boolResult)
        : parts.some((boolResult) => boolResult);
    }
  }

  component = _Component;

  applyCriteriaFromStorage(criteria: Partial<BondFilterCriteria>): Partial<BondFilterCriteria> {
    const rawBothOrOne = getSessionStorageString(FILTER_IS_AND_STORAGE_KEY);

    return {
      ...criteria,
      moodysRatingIndex: getSessionStorageNumber(FILTER_MOODY_STORAGE_KEY),
      sAndPratingIndex: getSessionStorageNumber(FILTER_SP_STORAGE_KEY),
      moodysOrSandPRatingIndex: getSessionStorageNumber(FILTER_MOODY_AND_SP_STORAGE_KEY),
      separateRatings: getSessionStorageBoolean(FILTER_SEPARATE_STORAGE_KEY),
      bothOrOne: rawBothOrOne === "both" ? "both" : rawBothOrOne === "one" ? "one" : undefined,
    };
  }

  storeCriteriaToStorage(criteria: BondFilterCriteria, defaultCriteria: BondFilterCriteria): void {
    if (criteria.moodysRatingIndex === defaultCriteria.moodysRatingIndex) {
      removeItemFromSessionStorage(FILTER_MOODY_STORAGE_KEY);
    } else {
      saveNumberToSessionStorage(FILTER_MOODY_STORAGE_KEY, criteria.moodysRatingIndex);
    }

    if (criteria.sAndPratingIndex === defaultCriteria.sAndPratingIndex) {
      removeItemFromSessionStorage(FILTER_SP_STORAGE_KEY);
    } else {
      saveNumberToSessionStorage(FILTER_SP_STORAGE_KEY, criteria.sAndPratingIndex);
    }

    if (criteria.moodysOrSandPRatingIndex === defaultCriteria.moodysOrSandPRatingIndex) {
      removeItemFromSessionStorage(FILTER_MOODY_AND_SP_STORAGE_KEY);
    } else {
      saveNumberToSessionStorage(FILTER_MOODY_AND_SP_STORAGE_KEY, criteria.moodysOrSandPRatingIndex);
    }

    if (criteria.separateRatings === defaultCriteria.separateRatings) {
      removeItemFromSessionStorage(FILTER_SEPARATE_STORAGE_KEY);
    } else {
      saveBooleanToSessionStorage(FILTER_SEPARATE_STORAGE_KEY, criteria.separateRatings);
    }

    if (criteria.bothOrOne === defaultCriteria.bothOrOne) {
      removeItemFromSessionStorage(FILTER_IS_AND_STORAGE_KEY);
    } else {
      saveStringToSessionStorage(FILTER_IS_AND_STORAGE_KEY, criteria.bothOrOne);
    }
  }
}
